import { useEffect, useRef, useState } from 'react';
import { Anchor, Box, Center, Loader, Stack, Text } from '@mantine/core';
import { useAuth, getClaims } from '@workos-inc/authkit-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { resolveProtectedRoute, resolveHostRedirect, isCloudhouseHost } from '../lib/routing';
import { orgSlugFromClaims } from '../lib/orgSlug';
import { useHostReady, type HostReadyState } from '../lib/useHostReady';
import { recordBounce, clearBounceGuard } from '../lib/hostBounceGuard';
import { HostReadyBanner } from './HostReadyBanner';
import { logEvent } from '../lib/telemetry';
import { peekTrialIntent, withTrialIntent } from '../lib/trialIntent';
import { withPreview } from '../lib/preview';

// What to do about a pending cross-host bounce, given why we want to bounce and whether the
// destination host is answering. Pure, so the component below stays a thin hooks → render wiring.
//
// - parked: a hub_to_slug user whose host isn't up on the first probe stays on hub with a banner.
//   'recovered' = it came up while they waited → offer a switch, don't yank them mid-task.
// - autoBounce: hub is always live (slug_to_hub); a wrong-slug host bounces as soon as it is up;
//   a hub_to_slug host bounces seamlessly ONLY when it was up on the first probe ('ready').
function resolveBounceState(reason: string | undefined, hostReady: HostReadyState) {
  const isSlugToHub = reason === 'slug_to_hub';
  const isHubToSlug = reason === 'hub_to_slug';
  const isWrongSlug = reason === 'wrong_slug';
  const hostUp = hostReady === 'ready' || hostReady === 'recovered';
  const parked =
    isHubToSlug &&
    (hostReady === 'unreachable' || hostReady === 'timed-out' || hostReady === 'recovered' || hostReady === 'stopped');
  const autoBounce = isSlugToHub || (isWrongSlug && hostUp) || (isHubToSlug && hostReady === 'ready');
  return { isSlugToHub, isHubToSlug, hostUp, parked, autoBounce };
}

// Waiting on a freshly-created org host to come up gets its own copy; provisioning that runs
// long shows a reassurance instead of a bare, unexplained spinner. (hub_to_slug parks on hub
// with a banner instead of reaching the timed-out message.)
const gateSupportLink = (
  <Anchor href="mailto:support@cloudhouse.com" c="dimmed" underline="always">
    support@cloudhouse.com
  </Anchor>
);

function GateScreen({ settingUp, timedOut, stopped }: Readonly<{ settingUp: boolean; timedOut: boolean; stopped: boolean }>) {
  if (settingUp && stopped) {
    return (
      <Center h="100vh" px="md">
        <Text ta="center" c="dimmed">
          Setting up your organisation is taking much longer than it should, so we've stopped checking
          automatically. Refresh the page to check again, or contact {gateSupportLink}.
        </Text>
      </Center>
    );
  }
  if (settingUp && timedOut) {
    return (
      <Center h="100vh" px="md">
        <Text ta="center" c="dimmed">
          Setting up your organisation is taking longer than expected. We'll keep checking and take you
          straight in the moment it's ready — or contact {gateSupportLink} if it persists.
        </Text>
      </Center>
    );
  }
  return (
    <Center h="100vh" role="progressbar" aria-label={settingUp ? 'Setting up your organisation' : 'Loading'}>
      <Stack align="center" gap="md">
        <Loader size="xl" />
        {settingUp && <Text c="dimmed">Setting up your organisation…</Text>}
      </Stack>
    </Center>
  );
}

// Single source of truth for no-org_id routing. org membership comes straight from
// the WorkOS SDK (organizationId maps the access-token org_id claim). All branching
// lives in resolveProtectedRoute (pure, unit-tested); this only wires hooks →
// decision → render. EnterpriseHub does NOT re-check org, so there is no loop.
export function ProtectedRoute() {
  const { user, isLoading, organizationId, getAccessToken } = useAuth();
  const location = useLocation();

  // org_slug lives in a custom claim the SDK does not surface directly; read it via
  // getClaims. undefined = not resolved yet; string|null = resolved (null when the claim is
  // absent, e.g. before the org_slug claim ships — the guard then no-ops).
  const [orgSlug, setOrgSlug] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let active = true;
    void (async () => {
      // Wait for sign-in to settle before resolving the slug. During the load window
      // organizationId is transiently null; treating that as "no org" would bounce a user off
      // their own freshly-provisioned host before the session restores — an endless loop.
      if (isLoading) return;
      if (!organizationId) {
        setOrgSlug(null);
        return;
      }
      const token = await getAccessToken?.();
      if (!active) return;
      setOrgSlug(token ? orgSlugFromClaims(getClaims(token)) : null);
    })();
    return () => { active = false; };
  }, [isLoading, organizationId, getAccessToken]);

  // No host decision until sign-in has settled AND the slug has been read. This is what stops the
  // org-host ↔ hub redirect loop: a not-yet-loaded session must not read as "slug-less".
  const hostRedirect = (isLoading || orgSlug === undefined)
    ? null
    : resolveHostRedirect(globalThis.location.hostname, orgSlug, location.pathname, location.search);

  const reason = hostRedirect?.reason;

  // A bounce to a slug host can target a freshly-created org whose edge isn't provisioned yet, so
  // poll until it answers. The neutral hub host is always live, so a slug_to_hub bounce skips the
  // poll — gateHost stays null and useHostReady is 'idle'.
  const gateHost = hostRedirect && reason !== 'slug_to_hub' ? new URL(hostRedirect.to).host : null;
  const hostReady = useHostReady(gateHost);

  // Derived from the current hostReady value, so no sticky state is needed.
  const { isHubToSlug, hostUp, parked, autoBounce } = resolveBounceState(reason, hostReady);
  const autoBounceReady = !!hostRedirect && autoBounce;

  const [loopBroken, setLoopBroken] = useState(false);

  const pollStartRef = useRef<number | null>(null);
  useEffect(() => { pollStartRef.current = gateHost ? Date.now() : null; }, [gateHost]);

  const bounceTo = (to: string, trigger: 'auto' | 'user_switch') => {
    const { allow, count } = recordBounce();
    if (!allow) {
      logEvent('cloudhouse.enterprise.slugguard.loop_broken', 'Stopped a cross-host redirect loop', {
        target_host: new URL(to).host, bounce_count: count,
      });
      setLoopBroken(true);
      return;
    }
    logEvent('cloudhouse.enterprise.slugguard.bounced', 'Bounced user to their own org host', {
      requested_slug: globalThis.location.hostname.split('.')[0],
      org_slug: orgSlug,
      reason,
      trigger,
    });
    // Cross-origin full-page navigation to the user's own org host. A pending trial link (its
    // capture and attempt times included) goes along whatever this host's flag says: the flag is
    // evaluated afresh on the other origin, where the intent stays inert unless it reads on, and
    // the arrival strips it from the address bar. A preview session stays in preview there.
    globalThis.location.assign(withPreview(withTrialIntent(to, peekTrialIntent()), document.cookie));
  };

  useEffect(() => {
    if (!hostRedirect || !autoBounceReady || loopBroken) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- bounceTo sets loopBroken at most once, only when the cross-host bounce cap trips, so we render the app instead of an endless spinner
    bounceTo(hostRedirect.to, 'auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per destination once ready; hostRedirect/orgSlug are read at bounce time
  }, [hostRedirect?.to, autoBounceReady, loopBroken]);

  useEffect(() => {
    if (!isHubToSlug || hostReady !== 'unreachable' || !gateHost) return;
    logEvent('cloudhouse.enterprise.slugguard.parked_on_hub', 'Parked user on hub while their org host provisions', {
      org_slug: orgSlug, target_host: gateHost, reason,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per park; orgSlug read at park time
  }, [isHubToSlug, hostReady, gateHost]);

  useEffect(() => {
    if (!hostUp || !gateHost) return;
    logEvent('cloudhouse.enterprise.slugguard.host_ready', 'Org host became reachable', {
      org_slug: orgSlug,
      target_host: gateHost,
      waited_ms: pollStartRef.current ? Date.now() - pollStartRef.current : 0,
      parked: hostReady === 'recovered',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per host coming up
  }, [hostUp, gateHost]);

  useEffect(() => {
    if (hostReady !== 'timed-out' || !gateHost) return;
    logEvent('cloudhouse.enterprise.slugguard.host_timeout', 'Org host did not become reachable in time', {
      org_slug: orgSlug, target_host: gateHost,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per timeout
  }, [hostReady, gateHost]);

  useEffect(() => {
    if (hostReady !== 'stopped' || !gateHost) return;
    logEvent('cloudhouse.enterprise.slugguard.host_poll_stopped', 'Gave up polling the org host', {
      org_slug: orgSlug, target_host: gateHost,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per give-up
  }, [hostReady, gateHost]);

  const decision = resolveProtectedRoute(
    { isLoading, isSignedIn: !!user, organizationId: organizationId ?? null },
    location.pathname,
  );

  const shouldLogWelcome = decision.kind === 'redirect' && decision.logWelcome === true;
  const userId = user?.id ?? null;

  useEffect(() => {
    // Once per signed-in user per browser session. sessionStorage survives remounts on
    // navigation (a useRef would reset and re-fire) and covers the StrictMode double-
    // effect. Scoped to the user id so a second zero-org user signing in *in the same
    // tab* (sessionStorage isn't cleared on sign-out) still records their own routing.
    if (!userId) return;
    const KEY = `eh.routed_to_welcome:${userId}`;
    if (shouldLogWelcome && !sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, '1');
      // trace_id/span_id come from the App Insights operation context inside logEvent.
      logEvent('cloudhouse.enterprise.postsignin.routed_to_welcome', 'Routed zero-org user to /welcome');
    }
  }, [shouldLogWelcome, userId]);

  // Clear the loop guard once the user lands cleanly (no pending host bounce), so a normal
  // one-off bounce never counts toward the cap. Guarded on a settled slug so the transient
  // pre-resolve window (hostRedirect momentarily null) can't reset the counter every remount —
  // which would defeat the guard, since each loop hop is a fresh full-page load.
  useEffect(() => {
    if (!isLoading && orgSlug !== undefined && !hostRedirect && decision.kind === 'render') clearBounceGuard();
  }, [isLoading, orgSlug, hostRedirect, decision.kind]);

  // Park a new-org user on hub with a banner instead of blocking, once we know their host isn't
  // up yet. Everything else (loading, slug_to_hub, wrong_slug) still holds behind the loader.
  const showBanner = isHubToSlug && parked && !loopBroken;

  const onCloudhouseHost = isCloudhouseHost(globalThis.location.hostname);
  const awaitingSlug = !!organizationId && onCloudhouseHost && orgSlug === undefined;
  if (!loopBroken && (decision.kind === 'loading' || awaitingSlug || (hostRedirect && !showBanner))) {
    return <GateScreen settingUp={!!gateHost && hostReady !== 'ready'} timedOut={hostReady === 'timed-out'} stopped={hostReady === 'stopped'} />;
  }
  if (decision.kind === 'redirect') return <Navigate to={decision.to} replace />;
  return (
    <Box h={'100vh'} style={{ overflow: 'hidden', flexDirection: 'column' }} display={'flex'} data-testid="protected-route-outlet">
      {showBanner && (
        <HostReadyBanner
          ready={hostReady === 'recovered'}
          timedOut={hostReady === 'timed-out'}
          stopped={hostReady === 'stopped'}
          onSwitch={() => hostRedirect && bounceTo(hostRedirect.to, 'user_switch')}
        />
      )}
      <Outlet />
    </Box>
  );
}
